import type { Duration } from 'date-fns';
import { intervalToDuration } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';

// Threshold in hours - countdown only displays when time remaining is less than this value
const COUNTDOWN_THRESHOLD_HOURS = 48;
const THRESHOLD_MS = COUNTDOWN_THRESHOLD_HOURS * 60 * 60 * 1000;

interface CountdownState {
  hasDeadlinePassed: boolean;
  duration: Duration;
  totalHours: number;
}

const INITIAL_STATE: CountdownState = {
  hasDeadlinePassed: false,
  duration: {},
  totalHours: 0,
};

const EXPIRED_STATE: CountdownState = {
  hasDeadlinePassed: true,
  duration: {},
  totalHours: 0,
};

/**
 * Countdown hook for bonus expiry display with hour-based formatting.
 *
 * Handles countdowns that can span multiple days, displaying time in HH:MM:SS format
 * (e.g., "47:59:59") when within the 48-hour threshold.
 *
 * Performance optimizations:
 * - Uses a single state object to minimize re-renders
 * - Sleeps when outside threshold (no ticking until 48 hours before expiry)
 * - Only ticks every second when countdown is visible
 *
 * Behavior:
 * - When time remaining > 48 hours: Sleeps until threshold, shouldShowCountdown = false
 * - When time remaining <= 48 hours: Ticks every second, shows HH:MM:SS format
 * - When deadline passed: shouldShowCountdown = false, no more updates
 *
 * @param expirationDate - The date when the bonus expires
 * @returns Object with formatted countdown, deadline status, and visibility flag
 *
 * @example
 * ```tsx
 * const { timeLeft, hasDeadlinePassed, shouldShowCountdown } = useBonusCountdown(bonus.expiryDate);
 *
 * if (hasDeadlinePassed) return <ExpiredMessage />;
 * if (shouldShowCountdown) return <Countdown>{timeLeft}</Countdown>;
 * return <ExpiryDate>{format(bonus.expiryDate, 'M/d/yy')}</ExpiryDate>;
 * ```
 */
export function useBonusCountdown(expirationDate: Date) {
  const [state, setState] = useState<CountdownState>(INITIAL_STATE);

  // Normalize Date to timestamp to prevent infinite loops when new Date objects with same value are created
  const expirationTimestamp = useMemo(() => expirationDate.getTime(), [expirationDate]);

  // Use refs for timer IDs to ensure cleanup always has access to current values
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Cleanup function to clear all timers
    const cleanup = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    // Calculate current countdown state
    const calculateState = (): CountdownState => {
      const now = Date.now();

      if (now >= expirationTimestamp) {
        return EXPIRED_STATE;
      }

      const targetDate = new Date(expirationTimestamp);
      const duration = intervalToDuration({ start: new Date(now), end: targetDate });
      const totalHours = (duration.days || 0) * 24 + (duration.hours || 0);

      return {
        hasDeadlinePassed: false,
        duration,
        totalHours,
      };
    };

    // Start the 1-second interval for active countdown
    const startTicking = () => {
      // Clear any existing interval first
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Update immediately
      const currentState = calculateState();
      setState(currentState);

      if (currentState.hasDeadlinePassed) {
        return;
      }

      // Start ticking every second
      intervalRef.current = setInterval(() => {
        const newState = calculateState();
        setState(newState);

        // Stop ticking if expired
        if (newState.hasDeadlinePassed && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, 1000);
    };

    // Main initialization logic
    const initialize = () => {
      const now = Date.now();
      const msUntilExpiration = expirationTimestamp - now;

      // Already expired
      if (msUntilExpiration <= 0) {
        setState(EXPIRED_STATE);
        return;
      }

      // Within threshold - start ticking immediately
      if (msUntilExpiration <= THRESHOLD_MS) {
        startTicking();
        return;
      }

      // Outside threshold - calculate state once, then sleep until threshold
      setState(calculateState());

      const msUntilThreshold = msUntilExpiration - THRESHOLD_MS;
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        startTicking();
      }, msUntilThreshold);
    };

    initialize();

    return cleanup;
  }, [expirationTimestamp]);

  const { hasDeadlinePassed, duration, totalHours } = state;

  // Determine if we're within the bonus countdown threshold
  const isWithinThreshold = !hasDeadlinePassed && totalHours < COUNTDOWN_THRESHOLD_HOURS;

  // Format countdown as HH:MM:SS when within threshold
  const timeLeft = useMemo(() => {
    if (hasDeadlinePassed) {
      return '00:00:00';
    }

    if (isWithinThreshold) {
      // Hour format: show total hours even when > 24h (e.g., "47:30:15")
      return [
        String(totalHours).padStart(2, '0'),
        String(duration.minutes || 0).padStart(2, '0'),
        String(duration.seconds || 0).padStart(2, '0'),
      ].join(':');
    }

    // Outside threshold: return standard format (not typically displayed)
    if (duration.days && duration.days > 0) {
      return `${duration.days}d ${duration.hours}h`;
    }

    return [
      String(duration.hours || 0).padStart(2, '0'),
      String(duration.minutes || 0).padStart(2, '0'),
      String(duration.seconds || 0).padStart(2, '0'),
    ].join(':');
  }, [hasDeadlinePassed, isWithinThreshold, totalHours, duration]);

  return {
    timeLeft,
    hasDeadlinePassed,
    shouldShowCountdown: isWithinThreshold,
  };
}
